import React, { useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';
import { useInstitutionalResources } from '../contexts/InstitutionalDataContext';

const InstitutionalAdminDashboard = () => {
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'institutional_admin') {
      window.location.replace('/');
    }
  }, []);

  // Pull both datasets from the shared cache; page renders instantly if we've
  // already visited another institutional page this session.
  const { entries, loading, error } = useInstitutionalResources(['analytics', 'programs']);
  const analytics = entries.analytics?.data || null;
  const programs = entries.programs?.data || [];

  const breadcrumbItems = useMemo(
    () => [
      { href: '/institutional-admin/dashboard', label: 'Inst. Admin' },
      { label: 'Dashboard' },
    ],
    []
  );

  const managementHref = '/institutional-admin/user-program-management';

  const { directorCount, programsWithoutDirector } = useMemo(() => {
    const directorIds = new Set(
      programs.filter((pr) => pr.director?.id).map((pr) => pr.director.id)
    );
    return {
      directorCount: directorIds.size,
      programsWithoutDirector: programs.filter((pr) => !pr.director?.id).length,
    };
  }, [programs]);

  const statCards = useMemo(() => {
    const summary = analytics?.summary;
    if (!summary) return [];
    return [
      { label: 'Programs', count: summary.totalPrograms, icon: 'bi-mortarboard', color: 'success', href: managementHref },
      { label: 'Directors', count: directorCount, icon: 'bi-person-badge', color: 'info', href: managementHref },
      { label: 'Residents', count: summary.totalResidents, icon: 'bi-people', color: 'primary', href: managementHref },
      { label: 'Attendings', count: summary.totalAttendings, icon: 'bi-person-lines-fill', color: 'warning', href: managementHref },
      { label: 'Total users', count: summary.totalUsers, icon: 'bi-people-fill', color: 'dark', href: managementHref },
    ];
  }, [analytics, directorCount]);

  return (
    <div className="d-flex vh-100 overflow-hidden bg-light">
      <SuperAdminSidebar variant="institutional" />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        <div className="flex-grow-1 p-4">
          <Container fluid>
            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            {loading ? (
              <div className="d-flex align-items-center"><Spinner size="sm" className="me-2" /> Loading...</div>
            ) : (
              <>
                <h4 className="mb-2">Institution overview</h4>
                {analytics?.institution?.name && (
                  <p className="text-muted mb-4">{analytics.institution.name}</p>
                )}
                <Row xs={2} md={3} lg={5} xl={5} className="g-3">
                  {statCards.map((stat) => (
                    <Col key={stat.label}>
                      <Card className="h-100 border-0 shadow-sm stat-card-modern" style={{ transition: 'transform 0.2s' }}>
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <i className={`bi ${stat.icon} fs-4 text-${stat.color}`}></i>
                            <span className="fs-2 fw-bold">{stat.count}</span>
                          </div>
                          <Card.Title className="small mb-auto">{stat.label}</Card.Title>
                          <Button variant={`outline-${stat.color}`} size="sm" href={stat.href} className="mt-2">
                            Manage
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {programsWithoutDirector > 0 && (
                  <Alert variant="warning" className="mt-4">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>{programsWithoutDirector}</strong> program{programsWithoutDirector !== 1 ? 's do' : ' does'} not have an assigned director.{' '}
                    <a href={managementHref}>Open user/program management</a>
                  </Alert>
                )}
              </>
            )}
          </Container>
        </div>
      </div>
    </div>
  );
};

export default InstitutionalAdminDashboard;
